import './NPCEncounterCard.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.6 (2026-06-06) — NPCEncounterCard.
 *
 * Pure-presentation render of a `force_npc_encounter_roll` dispatch
 * action. 2d6 roll on the Ch 14 NPC Encounter master table (hot =
 * mission-related locale, cold = wandered astray) → encounter id.
 *
 * Schema 1-to-1 with the V9.1.b tool args at routes/dispatch.py:669-704:
 *   - area_type: enum {hot, cold} (required)
 *   - modifier: integer (optional, default 0). Examples: Major Villain
 *     area +1, Heavy disguise -1.
 *   - rationale: GM voice-line ≤30 words (optional)
 *
 * Surfaces the SETUP (area type + modifier formula). Resolved roll +
 * encounter resolve downstream — pass `resolvedRoll` (2d6+mod final
 * value) + `resolvedEncounterId` / `resolvedEncounterLabel` to surface
 * the outcome; omit to show the pending state.
 */

export type EncounterAreaType = 'hot' | 'cold';

export interface NPCEncounterCardProps {
  areaType: EncounterAreaType;
  /** Pre-roll 2d6 modifier. Default 0. */
  modifier?: number;
  rationale?: string;
  /** Resolved final value of 2d6 + modifier. Omit/null → pending. */
  resolvedRoll?: number | null;
  /** Resolved encounter id from master table. */
  resolvedEncounterId?: string | null;
  /** Humanized encounter label (e.g. "Major Villain", "Local Police"). */
  resolvedEncounterLabel?: string | null;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

const AREA_LABEL: Record<EncounterAreaType, string> = {
  hot: 'Hot area',
  cold: 'Cold area',
};

const AREA_DESCRIPTION: Record<EncounterAreaType, string> = {
  hot: 'Mission-related locale.',
  cold: 'Wandered astray.',
};

function formatModifier(mod: number): string {
  if (mod === 0) return '';
  return mod > 0 ? ` + ${mod}` : ` − ${Math.abs(mod)}`;
}

export function NPCEncounterCard({
  areaType,
  modifier = 0,
  rationale,
  resolvedRoll,
  resolvedEncounterId,
  resolvedEncounterLabel,
  onDismiss,
  testidPrefix = 'br-npc-encounter',
}: NPCEncounterCardProps) {
  const hasResolved =
    typeof resolvedRoll === 'number' &&
    typeof resolvedEncounterId === 'string' &&
    resolvedEncounterId.length > 0;
  const formula = `2d6${formatModifier(modifier)}`;
  const labelToShow =
    resolvedEncounterLabel !== undefined && resolvedEncounterLabel !== null && resolvedEncounterLabel.length > 0
      ? resolvedEncounterLabel
      : resolvedEncounterId;

  return (
    <aside
      className="br-npc-encounter"
      data-testid={testidPrefix}
      data-area-type={areaType}
      data-modifier={modifier}
      data-state={hasResolved ? 'resolved' : 'pending'}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-npc-encounter-header">
        <span className="br-npc-encounter-label">NPC Encounter</span>
        <span
          className="br-npc-encounter-area-chip"
          data-testid={`${testidPrefix}-area-chip`}
          data-area-type={areaType}
        >
          {AREA_LABEL[areaType]}
        </span>
      </header>

      <p
        className="br-npc-encounter-area-desc"
        data-testid={`${testidPrefix}-area-desc`}
      >
        {AREA_DESCRIPTION[areaType]}
      </p>

      <div
        className="br-npc-encounter-formula"
        data-testid={`${testidPrefix}-formula`}
        data-formula={formula}
      >
        <span className="br-npc-encounter-formula-label">Roll</span>
        <span className="br-npc-encounter-formula-value br-label">{formula}</span>
      </div>

      {hasResolved ? (
        <div
          className="br-npc-encounter-result"
          data-testid={`${testidPrefix}-result`}
          data-encounter-id={resolvedEncounterId}
        >
          <div
            className="br-npc-encounter-roll-cell"
            data-testid={`${testidPrefix}-resolved-roll`}
          >
            <span className="br-npc-encounter-roll-label">Roll</span>
            <span className="br-npc-encounter-roll-value br-label">{resolvedRoll}</span>
          </div>
          <div
            className="br-npc-encounter-encounter-cell"
            data-testid={`${testidPrefix}-encounter-label`}
          >
            <span className="br-npc-encounter-encounter-label-pretitle">Encounter</span>
            <span className="br-npc-encounter-encounter-label-value">{labelToShow}</span>
          </div>
        </div>
      ) : (
        <p
          className="br-npc-encounter-pending"
          data-testid={`${testidPrefix}-pending`}
        >
          Awaiting 2d6 roll…
        </p>
      )}

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-npc-encounter-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
